import { createResponse } from "./cloudFunctions"
import * as palette from "../../components/symbols/palette"
import { getPromptLayers } from "./chatPromptServices"
import { createChatPromptData } from "./modelEditServices"
import * as diagramToPromptServices from "./diagramToPromptServices"
import * as Roles from "./roleServices"
import _ from "lodash"
import getContextFactsSchema from "./schemas/get_context_facts.json"
import createTitleAndSummary from "./schemas/create_title_and_summary.json"
import sortDiagrams from "./schemas/sort_diagrams.json"
import getSuggestedTagsSchema from "./schemas/get_suggested_tags.json"
import sortViewsSchema from "./schemas/sort_views.json"

const GPT_5 = "gpt-5-mini"
const GPT_5_2 = "gpt-5.2"

const FUNCTION_RESEARCH_CONTEXT = "research_context"
const FUNCTION_GENERATE_PROMPTS = "generate_prompts"
const FUNCTION_GENERATE_INSIGHTS_AND_ACTIONS = "generate_insights_and_actions"
const FUNCTION_CREATE_TITLE_AND_SUMMARY = "create_title_and_summary"
const FUNCTION_GET_BATCH_DESCRIPTIONS = "get_batch_descriptions"
const FUNCTION_GET_SUGGESTED_TAGS = "get_suggested_tags"
const FUNCTION_GET_AUTO_COUNT_OF_NEXT_LEVEL = "get_auto_count_of_next_level"
const FUNCTION_GET_QUESTIONS_COMMON = "get_questions_common"
const FUNCTION_GET_MODEL_QUESTIONS = "get_model_questions"
const FUNCTION_GET_EXTENDED_QUESTIONS = "get_extended_questions"
const FUNCTION_SORT_QUESTIONS = "sort_questions"
const FUNCTION_SORT_VIEWS = "sort_views"
const FUNCTION_CREATE_TEMPLATE = "create_template"

const IS_AIM_TRIAL = true

// const AVERAGE_CHARS_PER_WORD = 6

const MODEL_USAGE = {
  [FUNCTION_RESEARCH_CONTEXT]: {
    model: GPT_5_2,
  },
  [FUNCTION_GENERATE_PROMPTS]: {
    model: GPT_5,
  },
  [FUNCTION_GENERATE_INSIGHTS_AND_ACTIONS]: {
    model: GPT_5_2,
  },
  [FUNCTION_CREATE_TITLE_AND_SUMMARY]: {
    model: GPT_5,
  },
  [FUNCTION_GET_BATCH_DESCRIPTIONS]: {
    model: GPT_5,
  },
  [FUNCTION_GET_SUGGESTED_TAGS]: {
    model: GPT_5,
  },
  [FUNCTION_GET_AUTO_COUNT_OF_NEXT_LEVEL]: {
    model: GPT_5,
  },
  [FUNCTION_GET_QUESTIONS_COMMON]: {
    model: GPT_5_2,
  },
  [FUNCTION_GET_MODEL_QUESTIONS]: {
    model: GPT_5_2,
  },
  [FUNCTION_GET_EXTENDED_QUESTIONS]: {
    model: GPT_5_2,
  },
  [FUNCTION_SORT_QUESTIONS]: {
    model: GPT_5,
  },
  [FUNCTION_SORT_VIEWS]: {
    model: GPT_5,
  },
  [FUNCTION_CREATE_TEMPLATE]: {
    model: GPT_5_2,
  },
}

const POLICY_TRIAL = "trial"
const POLICY_PAID = "paid"

const QTY_SELECTION_ANY = "any"
const QTY_SELECTION_UPTO = "upto"
const QTY_SELECTION_FIXED = "fixed"

const MIN_CONTEXT_ITEMS = "min_context_items"
const MAX_CONTEXT_ITEMS = "max_context_items"
const MAX_MODEL_QUESTIONS = "max_model_questions"
const MAX_OPEN_QUESTIONS = "max_open_questions"
const MAX_DESCRIPTION_WORDS = "max_description_words"
const MAX_ELEMENTS = "max_elements"
const MAX_VIEWS = "max_views"
const MAX_ACTIONS = "max_actions"
const MAX_INSIGHTS = "max_insights"
const CAN_USE_O3 = "can_use_o1"

const AI_USAGE_POLICY = {
  [POLICY_PAID]: {
    [MIN_CONTEXT_ITEMS]: 8,
    [MAX_CONTEXT_ITEMS]: undefined,
    max_elements: undefined,
    [MAX_MODEL_QUESTIONS]: undefined,
    [MAX_OPEN_QUESTIONS]: undefined,
    // in words
    [MAX_DESCRIPTION_WORDS]: 50,
    [MAX_ELEMENTS]: 30,
    [MAX_VIEWS]: 30,
    [MAX_INSIGHTS]: undefined,
    [MAX_ACTIONS]: undefined,
    [CAN_USE_O3]: true,
  },
  [POLICY_TRIAL]: {
    [MAX_CONTEXT_ITEMS]: 2,
    max_elements: 5,
    [MAX_MODEL_QUESTIONS]: 3,
    [MAX_OPEN_QUESTIONS]: 3,
    [MAX_DESCRIPTION_WORDS]: 20,
    [MAX_ELEMENTS]: 5,
    [MAX_VIEWS]: 5,
    [MAX_INSIGHTS]: 2,
    [MAX_ACTIONS]: 2,
    [CAN_USE_O3]: false,
  },
}

const getModel = ({ roles, funcName }) => {
  if (!roles) {
    console.error("Roles not provided")
  }

  return MODEL_USAGE[funcName]?.model || GPT_5
}

const getAIUsageParam = ({ roles, paramName }) => {
  const isPaid = roles.includes(Roles.AIM_AI)
  const policy = AI_USAGE_POLICY[isPaid ? POLICY_PAID : POLICY_TRIAL]
  const result = policy[paramName]
  // console.log("%cAI usage policy", "color:yellow", {
  //   policy,
  //   paramName,
  //   result,
  // })
  return result
}

// For creating embeddings
const TEXT_EMBEDDING_ADA_002 = "text-embedding-ada-002"

const getSuggestedViewPrompt = async ({
  purpose,
  elementType,
  question,
  parentType,
  roles,
}) => {
  // ---- Build prompt (rules-first) ----
  const lines = `
SYSTEM RULES (follow exactly):
- Output MUST be a single JSON object that validates against the provided schema (one field: 'prompt').
- Do NOT include any extra prose, explanations, notes, or questions.
- Do NOT include the objective or contextual details inside the generated prompt.
- Do NOT include example data or sample response values.
- Do NOT use phrases like 'Consider' or 'Think about' inside the prompt.

TASK:
- Create a concise prompt to answer the question: ${question}

CONTEXT FOR REASONING ONLY (do not include in the prompt text):
- Overall objective: ${purpose}

INSTRUCTIONS FOR THE PROMPT YOU WILL OUTPUT:
- The prompt should request a collection of ${palette.formatLabel(
    elementType
  )} elements.
- Keep the prompt tightly aligned to the question; do not restate the objective.
- Write the prompt in under 200 words.
- Do not prescribe or list response data fields; focus on intent, not output schema.
- Stay at an appropriate level of abstraction so the elements are relevant to the question and purpose without adding unnecessary technical or detailed attributes.
- IMPORTANT: Do not include how many elements to generate.
- IMPORTANT: Write the prompt so that the model’s response will be a list/collection of ${palette.formatLabel(
    elementType
  )} elements (not a free-form description). The returned set will be arranged into a homogeneous block of ${palette.formatLabel(
    elementType
  )} elements.`
    .split("\n")

  console.log("lines", lines)

  if ([palette.WORK_PACKAGE, palette.DELIVERABLE].includes(elementType)) {
    lines.push(
      "- Additional rule: Do not make reference to any timelines or completion dates in the prompt."
    )
  }

  if (parentType) {
    lines.push(
      `- Additional note: The view will be a child of a '${palette.formatLabel(
        parentType
      )}' element.`
    )
  }

  const prompt = lines.join("\n")

  // ---- Minimal JSON schema for structured output ----
  const jsonSchema = {
    name: "suggest_view_prompt",
    description:
      "Return a single field 'prompt' suitable for pasting into ChatGPT.",
    parameters: {
      type: "object",
      properties: {
        prompt: {
          type: "string",
          description:
            "The final prompt for the user to paste into ChatGPT. Do not include objective/context, examples, sample values, or meta-text.",
          minLength: 10,
        },
      },
      required: ["prompt"],
      additionalProperties: false,
    },
  }

  // ---- Call Responses via your callable wrapper ----
  const result = await createResponse({
    prompt,
    model: "gpt-5-mini", // more reliable than nano for strict JSON; override if you prefer
    reasoning: { effort: "low" }, // keeps it focused/non-chatty
    jsonSchema, // your helper maps to text.format for the Responses API
    store: true,
  })

  // ---- Parse JSON output ----
  let out = { prompt: "" }
  try {
    const outputText =
      result?.response?.output_text ?? result?.output_text ?? ""
    const parsed = JSON.parse(outputText)
    if (parsed && typeof parsed.prompt === "string") {
      out = parsed
    }

    console.log("%cgenerate prompt", "color:yellow", { out })
  } catch (e) {
    console.warn("Failed to parse suggested view prompt JSON", e)
  }

  return { elementType, ...out }
}

const getViewSummary = (view) => ({
  name: view.name,
  description: view.description,
  prompts: view.prompt_history,
  types: Array.from(new Set(view.elements.map((el) => el.type)))
    .map((typeId) =>
      palette.formatLabel(palette.getElementTypeByIndex(typeId).name)
    )
    .join(", "),
})

const getObjectiveAndContextMessages = ({ viewSet }) => {
  const messages = [
    `You are a helpful AI assistant that is an expert about achieving the following objective: ${viewSet.purpose}`,
  ]

  if (viewSet.view_context?.length > 0) {
    const extendedContext = [
      `The extended context information is as follows and this can help inform your responses:`,
      JSON.stringify(viewSet.view_context, null, 2),
    ]
    messages.push(...extendedContext)
  }

  messages.push(
    `Do not respond to this message. This is just to load the context in which views will be created.`
  )

  return messages
}

const suggestElementType = async ({ purpose, prompt }) => {
  // Build the allowed list straight from your palette
  const allowedTypes = (palette.getElements?.() || []).map((el) => el.name)

  // ---- Build prompt (rules-first) ----
  const lines = [
    "SYSTEM RULES (follow exactly):",
    "- Output MUST be a single JSON object that validates against the provided schema (fields: 'element_type' and optional 'reason').",
    "- Do NOT include any extra prose, explanations, or clarifying questions.",
    "- 'element_type' MUST be one of the allowed ArchiMate element types provided below.",
    "",
    "** Task **",
    "- Choose the single best ArchiMate element type the user should create a view of, based primarily on the prompt.",
    "",
    "** Inputs **",
    `- User prompt: ${JSON.stringify(prompt)}`,
  ]

  if (purpose) {
    lines.push(
      `- Objective context (for your reasoning only): ${JSON.stringify(
        purpose
      )}`
    )
  }

  lines.push(
    "",
    "** Allowed ArchiMate element types **",
    JSON.stringify(allowedTypes),
    "",
    "** Output format **",
    `- Return exactly: { "element_type": "<one_of_allowed_types>", "reason": "<short why (optional)>" }`
  )

  const finalPrompt = lines.join("\n")

  // ---- JSON schema for structured output ----
  const jsonSchema = {
    name: "suggest_element_type",
    description:
      "Select a single ArchiMate element type from the allowed list and optionally include a brief reason.",
    parameters: {
      type: "object",
      properties: {
        element_type: {
          type: "string",
          description: "One of the allowed ArchiMate element names.",
          enum: allowedTypes, // enforce palette-valid values
        },
        reason: {
          type: "string",
          description: "Brief justification (optional).",
          minLength: 5,
        },
      },
      required: ["element_type", "reason"],
      additionalProperties: false,
    },
  }

  const params = {
    prompt: finalPrompt,
    model: "gpt-5-mini", // mirrors your working example
    reasoning: { effort: "low" }, // concise, focused
    jsonSchema, // STRICT is applied inside your wrapper
    store: false,
  }

  console.log("suggesting element type", { params })

  // ---- Call Responses ----
  const result = await createResponse(params)

  console.log("%cresult", "color:lightblue", result)

  // ---- Parse + validate ----
  const outputText = result?.response?.output_text ?? result?.output_text ?? ""
  let parsed
  try {
    parsed = JSON.parse(outputText)
  } catch {
    // try to salvage JSON if model wrapped it in prose (shouldn’t, but be safe)
    const m = outputText.match(/\{[\s\S]*\}/)
    if (m) {
      try {
        parsed = JSON.parse(m[0])
      } catch {}
    }
  }

  if (!parsed || typeof parsed !== "object") {
    return { error: "parse_error", raw: outputText }
  }

  const { element_type, reason } = parsed

  // Defensive validation in case model drifted
  if (!element_type || !allowedTypes.includes(element_type)) {
    return {
      error: "invalid_element_type",
      detail: {
        received: element_type,
        allowedSample: allowedTypes.slice(0, 12),
      },
      raw: parsed,
    }
  }

  // Return a clean, predictable shape
  return { element_type, reason: typeof reason === "string" ? reason : "" }
}

const generateInsightsAndActions = async ({ views, purpose, roles }) => {
  const gptModel = getModel({
    roles: roles,
    funcName: FUNCTION_GENERATE_INSIGHTS_AND_ACTIONS,
  })

  // Build the same diagram details you had before (first view used below)
  const diagramDetails = views.map((view) => {
    const promptData = createChatPromptData({
      currentView: view,
      selectedItemId: undefined,
      hiddenProps: [],
    })

    const result = getPromptLayers(promptData)

    const diagramDescription = diagramToPromptServices.createChatPrompt({
      promptData,
      promptLayers: result,
      includeProperties: true,
      includeDoco: true,
    })

    console.log("%cprompt data for view", "color:lightgreen", {
      view: view.name,
      diagramDescription,
      purpose,
    })

    return {
      id: view.id,
      name: view.name,
      direct_statement: view.direct_statement,
      prompt_history: view.prompt_history,
      text_response: view.text_response || "",
      element_types: Array.from(
        new Set(
          view.elements.map((el) => palette.getElementTypeByIndex(el.type).name)
        )
      ),
      diagram: diagramDescription,
    }
  })

  if (!diagramDetails.length) {
    return { diagrams: [] }
  }

  // Use only the first diagram (same as original)
  const d0 = diagramDetails[0]

  const maxActions = getAIUsageParam({ roles, paramName: MAX_ACTIONS })
  const maxInsights = getAIUsageParam({ roles, paramName: MAX_INSIGHTS })

  // === Prompt kept to your original wording/structure ===
  const messageLines = [
    `** Task **
      - Review the diagram and create 3-6 insights and actions that will help the owner of the objective achieve their objective.
      - The actions provided should be easy to follow and stated in a way that helps take specific and practical steps towards the objective.
      - IMPORTANT: Each action MUST be standalone and described in a single entry not split over multiple lines.
      - IMPORTANT: Actions should be phrased succinctly yet clearly and ideally no more than 50 words.
      - The insights provided can be drawn from the information provided below, and also supplemented with your own knowledge and experience.
      - Order the insights so that the most important insights are presented first.

    ** Objective **
      ${purpose}

    ** Inputs **
      - For each diagram I will provide a name, element_types, direct_statement, text_response, and prompts used to create the view.
      - 'element_types' are the ArchiMate element types that are used in the view.
      - 'prompts' are the prompts that were used to generate the view.
      - 'direct_statement' is a crisp summary of the view that clearly states why this part is crucial for achieving the main objective.
      - 'text_response' is further research that you can use in addition to the diagram content and element_types information.
      - 'importance' is a measure of how crucial the view is to achieving the main objective, and also explains why you might fail to achieve the objective if this view is not considered.

    ** Diagram Inputs Information **
      This information is for the diagram: '${d0.name} [diagram_id: ${d0.id}]'.
      - element types used in view: ${d0.element_types.join(", ")}
      - prompts used to create the view: ${d0.prompt_history.join(", ")}

    ** Diagram **
      This is the diagram that you need to review and provide insights and actions for:
    `,
    ...d0.diagram,
    `** Diagram Text Response **
      This is extra information that you can use to help you provide insights and actions for the diagram:
      ${d0.text_response}
    `,
  ]

  const prompt = messageLines.join("\n")

  console.log("%cprompt", "color:lightblue", { prompt })

  // === Single Responses call (no temperature) ===
  const res = await createResponse({
    prompt,
    model: gptModel,
    reasoning: { effort: "medium" },
    jsonSchema: sortDiagrams,
    store: false,
  })

  console.log("%cresp", "color:lightgreen", { resp: res })

  // === Safe parse with stable shape ===
  const outputText = res?.response?.output_text ?? res?.output_text ?? ""

  let createInsightsActionsResult
  try {
    createInsightsActionsResult = outputText.trim()
      ? JSON.parse(outputText)
      : { diagrams: [] }
  } catch (e) {
    console.warn("Failed to parse sort_diagrams JSON", e, {
      outputSnippet: outputText.slice(0, 200),
    })
    createInsightsActionsResult = { diagrams: [] }
  }

  return createInsightsActionsResult
}

// Uses strict client-side `parameters` → server `schema` via createResponse
// Assumes sortViewsSchema is imported (the JSON Schema for the response)

const sortViewsForPresentation = async ({ views, purpose, roles }) => {
  const gptModel = getModel({ roles, funcName: FUNCTION_SORT_VIEWS })

  // Build a rich description for each view
  const diagramDetails = views.map((view) => {
    const promptData = createChatPromptData({
      currentView: view,
      selectedItemId: undefined,
      hiddenProps: [],
    })

    const result = getPromptLayers(promptData)

    const diagramDescription = diagramToPromptServices.createChatPrompt({
      promptData,
      promptLayers: result,
      includeProperties: true,
      includeDoco: true,
    })

    return {
      id: view.id,
      name: view.name,
      direct_statement: view.direct_statement,
      prompt_history: Array.isArray(view.prompt_history)
        ? view.prompt_history
        : [],
      element_types: Array.from(
        new Set(
          view.elements.map((el) => palette.getElementTypeByIndex(el.type).name)
        )
      ),
      diagram: diagramDescription,
    }
  })

  // Flatten each view into bullet-like lines for the prompt
  const diagramMessages = diagramDetails.map((detail) => {
    const lines = [
      `** Diagram: ${detail.name} [diagram_id: ${detail.id}]`,
      `- element_types: ${detail.element_types.join(", ")}`,
      `- prompts: ${detail.prompt_history.join(", ")}`,
      `- diagram: ${detail.diagram}`,
    ]
    if (detail.direct_statement) {
      lines.push(`- direct_statement: ${detail.direct_statement}`)
    }
    return lines
  })

  const promptLines = [
    `I want you to sort all diagrams that I provide in a logical order that effectively builds toward the overall goal of: ${purpose}`,
    `For each diagram I will provide a name, element_types, direct_statement, and prompts used to create the view.`,
    `The 'element_types' are the ArchiMate element types used in the view.`,
    `The 'prompts' are the prompts that were used to generate the view.`,
    `The 'direct_statement' is a crisp summary of the view that clearly states why this part is crucial for achieving the main objective.`,
    `The diagrams already exist; do not propose creating or changing elements.`,
    `There are ${views.length} diagrams to sort. The text '** Diagram' marks the beginning of a new diagram.`,
    `You MUST sort all ${views.length} provided diagrams.`,
    `You will be presenting to the owner of the objective; your presentation order and insights must help them achieve their objective.`,
    "",
    "Diagrams:",
    ...diagramMessages.flat(),
  ]

  const prompt = [
    "SYSTEM RULES:",
    "- Output MUST be a single JSON object that validates against the provided JSON Schema.",
    "- Only return fields defined by the schema.",
    "",
    "TASK:",
    ...promptLines,
  ].join("\n")

  const resp = await createResponse({
    prompt,
    model: "gpt-5-mini",
    jsonSchema: sortViewsSchema, // strict: callers pass parameters; helper converts to schema
    reasoning: { effort: "low" },
    store: false,
  })

  console.log("%cresponse", "color:lightgreen", { resp })

  const raw = resp?.response?.output_text
  if (!raw) return null

  const result = JSON.parse(raw)

  console.log("%csorted views result", "color:lightblue", { result })
  return result
}

const createConsultingSummary = async ({ currentView, objective, roles }) => {
  // Build the view payload you were already using
  const chatPromptData = createChatPromptData({
    currentView,
    hiddenProps: [],
    includeDoco: true,
  })

  const gptModel = getModel({
    roles,
    funcName: FUNCTION_CREATE_TITLE_AND_SUMMARY,
  })

  console.log("%cchat prompt data", "color:lightgreen", {
    currentView,
    chatPromptData,
  })

  // ---- Prompt (rules-first; JSON-only) ----
  const lines = [
    "SYSTEM RULES (follow exactly):",
    "- Output MUST be a single JSON object that validates against the provided schema.",
    "- Do NOT include any extra prose, explanations, or clarifying questions.",
    "",
    "Imagine you are creating an exec-level presentation.",
    `Provide a suggested slide title, and a punchy direct statement (<=20 words) that clearly states why this part is crucial for achieving the main objective of: ${objective}.`,
    "Also provide a short 'how_to_read' explanation (<=75 words).",
    "",
    "Analyse the following view information.",
    "--- BEGIN ---",
  ]

  if ((currentView?.elements || []).length > 0) {
    lines.push(JSON.stringify(chatPromptData))
  }
  if (currentView?.text_response) {
    lines.push(String(currentView.text_response))
  }

  lines.push("--- END ---")

  const prompt = lines.join("\n")

  // ---- Schema wiring (tool-style -> Responses json schema) ----
  const jsonSchema = {
    name: createTitleAndSummary.name,
    description: createTitleAndSummary.description,
    parameters:
      createTitleAndSummary.schema ?? createTitleAndSummary.parameters,
  }

  // ---- Call Responses via your callable wrapper ----
  const result = await createResponse({
    prompt,
    model: 'gpt-5-nano', // or e.g. "gpt-5-mini" if you prefer
    reasoning: { effort: "low" }, // focused / non-chatty
    jsonSchema, // your helper maps this to text.format
    store: true,
  })

  console.log("%cresponses result", "color:lightgreen", { result })

  // ---- Parse strict JSON output ----
  let consultingSummary = null
  try {
    const outputText =
      result?.response?.output_text ?? result?.output_text ?? ""
    consultingSummary = JSON.parse(outputText)
  } catch (e) {
    console.warn("Failed to parse consulting summary JSON", e)
    consultingSummary = null
  }

  console.log("consulting summary", { consultingSummary })
  return consultingSummary
}

const convertToMessages = (strArray) => {
  return strArray.map((str) => ({
    type: "text",
    text: str,
  }))
}

const getSuggestedTags = async ({ view, purpose, roles }) => {
  const gptModel = getModel({ roles, funcName: FUNCTION_GET_SUGGESTED_TAGS })

  const promptData = createChatPromptData({
    currentView: view,
    selectedItemId: undefined,
    hiddenProps: [],
  })
  const result = getPromptLayers(promptData)
  const diagramDescription = diagramToPromptServices.createChatPrompt({
    promptData,
    promptLayers: result,
    includeProperties: true,
    includeDoco: true,
  })

  const initialMessages = [
    `The purpose of suggesting tags is to help identify insights that help achieve this purpose: ${purpose}.`,
    `The tags you suggest should be relevant to the context and purpose provided and allow for a more detailed understanding of the diagram.`,
    `The prompt used to generate this diagram are as follows and should help you determine what tags could be most relevant: ${view.prompt_history.join(
      ", "
    )}.`,
    `Do not suggest tags that are the same as the diagram elements. The purpose of the tags is that their meaning is orthogonal to the diagram elements, and allows comparing and contrasting of diagram elements. For example, in a capability model it might be useful to add a Criticality tag, or in a Data Objects diagram it might be useful to add a Data Volume tag, and for a Stakeholder view it could be useful to add an Importance tag.`,
    `Different tags will be useful for different types of diagrams, so consider the type of diagram and its element types when suggesting tags.`,
    `The diagram you should examine and suggest tags for is as follows:`,
    `${diagramDescription}`,
  ]

  const prompt = [
    "SYSTEM RULES:",
    "- Output MUST be a single JSON object that validates against the provided JSON Schema.",
    "- Only return fields defined by the schema.",
    "",
    "TASK:",
    ...initialMessages,
  ].join("\n")

  const resp = await createResponse({
    prompt,
    model: "gpt-5-mini",
    jsonSchema: getSuggestedTagsSchema,
    reasoning: { effort: "low" },
    store: false,
  })

  console.log("%cresponse", "color:lightgreen", { resp })

  const raw = resp?.response?.output_text
  if (!raw) return []

  let suggestedTags
  try {
    suggestedTags = typeof raw === "string" ? JSON.parse(raw) : raw
  } catch {
    const match = raw.match(/\{[\s\S]*\}/)
    suggestedTags = match ? JSON.parse(match[0]) : null
  }

  console.log("%csuggested tags", "color:lightblue", { suggestedTags })

  if (!suggestedTags || !Array.isArray(suggestedTags.tags)) return []

  // --- Same formatting approach you used ---
  const formattedTags = {
    ...suggestedTags.tags,
    tags: suggestedTags.tags.map((tag) => {
      const newTag = {
        ...tag,
        name: tag.name.replace(/([A-Z])/g, " $1").trim(),
      }
      return newTag
    }),
  }

  return formattedTags.tags
}

const isPaid = ({ roles }) => {
  return roles.includes(Roles.AIM_AI)
}

const createViewSetContext = async ({
  setMessage,
  roles,
  contextInputFiles, // array of existing vector store ids: ["vs_xxx", ...]
  purpose,
}) => {
  const initialMessages = [
    `The user wants to achieve the following objective.

    ${purpose}

    ---------

    Think about what types of expertise is required to successfully plan and deliver the defined objective, 
    and provide context information relevant to those possibly broad skillsets.

    Provide specific information and facts that will help inform achieving my objective, including any substantial
    trends, immediate and substantial challenges, or top line imformation information to know about. I do not want you
    to identify areas that you think are important to know about, but rather provide the information directly.

    Also include some information on the broader context in which my objective is situated.
    If you provide statistics or numbers, please provide the source of the information.`,
  ]

  const maxContextItems = getAIUsageParam({
    roles,
    paramName: MAX_CONTEXT_ITEMS,
  })
  const minContextItems = getAIUsageParam({
    roles,
    paramName: MIN_CONTEXT_ITEMS,
  })

  if (minContextItems) {
    initialMessages.push(
      `Provide at least ${minContextItems} facts to help inform achieving my objective.`
    )
  }

  setMessage("Researching context...")

  console.log("%cvector store ids (contextInputFiles)", "color:lightgreen", {
    contextInputFiles,
  })

  // ---- Use existing vector stores directly (Responses API) ----
  let tools = []
  let fileInstruction = ""

  console.log("%cvector store ids (contextInputFiles)", "color:lightgreen", {
    contextInputFiles,
  })

  const vectorStoreIds = contextInputFiles?.map((item) => item.id)

  if (vectorStoreIds.length > 0) {
    tools = [{ type: "file_search", vector_store_ids: vectorStoreIds }]
    fileInstruction =
      `Read the attached file(s) available via file search and use relevant information to provide context facts. ` +
      `Where it makes sense, include a citation to the provided file(s) so the end user can see they were reviewed.`
  }

  const allMessages = [
    ...convertToMessages(initialMessages),
    ...(fileInstruction ? [{ type: "text", text: fileInstruction }] : []),
  ]

  console.log("%call messages", "color:lightgreen", { allMessages })

  const prompt = allMessages
    .map((m) => (typeof m === "string" ? m : m.text))
    .join("\n\n")

  const model = getModel({ roles, funcName: FUNCTION_RESEARCH_CONTEXT })

  console.log("%cprompt for context facts", "color:lightblue", { prompt, model })

  const result = await createResponse({
    prompt,
    tools, // only present if vector_store_ids exist
    reasoning: { effort: "low" }, // your helper normalizes this already
    jsonSchema: getContextFactsSchema, // <<— NOT `text`; your helper builds `text` internally
    store: true,
    model: "gpt-5-mini", // optional override if you don’t want the helper’s default
  })

  console.log("%cresponse result", "color:lightgreen", { result })

  // Your callable returns { status, response }; model output is in response.output_text
  let viewContext = []
  try {
    const outputText =
      result?.response?.output_text ??
      result?.output_text ?? // fallback if backend ever returns raw
      ""

    const parsed = JSON.parse(outputText)
    viewContext = parsed.context_items || []

    // Optional client-side cap:
    // if (maxContextItems) viewContext = viewContext.slice(0, maxContextItems);

    console.log("context", { viewContext })
  } catch (e) {
    console.warn("Failed to parse response JSON", e)
  }

  return viewContext
}

// Uses strict client-side `parameters` → server `schema` via createResponse

const checkModellingGoalCompleteness = async ({ purpose, views }) => {
  // Reuse your existing function definition to get the JSON Schema parameters
  const [toolDef] = createGptFunctionForCheckingGoalCompleteness()

  const viewSummary = views.map((v) => getViewSummary(v))
  const viewsFormatted = viewSummary.map(
    (v, index) => `${index + 1}. Name: ${v.name}, Types: (${v.types})`
  )

  const prompt = [
    "SYSTEM RULES:",
    "- Output MUST be a single JSON object that validates against the provided JSON Schema.",
    "- Only return fields defined by the schema.",
    "",
    "TASK:",
    "Consider the user's context and objective, best-practice Enterprise Architecture approaches guided by TOGAF, and ArchiMate elements.",
    "Assess if the user has created all the views they need to achieve their objective.",
    "",
    `objective: ${purpose}`,
    "existing views:",
    viewsFormatted.join("\n"),
  ].join("\n")

  const resp = await createResponse({
    prompt,
    model: "gpt-5-mini",
    jsonSchema: toolDef,
    reasoning: { effort: "low" },
    store: false,
  })

  console.log("%cresponse", "color:lightgreen", { resp })

  const raw = resp?.response?.output_text
  if (!raw) return null

  const obj = JSON.parse(raw)

  console.log("check modelling completeness", { obj })

  return obj
}

/**
 * Generate properties and tag against elements. Prop has name and description
 *
 * @param {*} elements | The elements to add tags to
 * @param {*} prop | The property to add
 */

const createElementProperties = async ({
  elements,
  elementProps,
  elementType,
  override,
  gptModel = GPT_5,
}) => {
  const typeDef = palette.getElementTypeByIndex(elementType)
  const prop = elementProps[0]

  const elementsToUse = override
    ? elements
    : elements.filter(
        (el) =>
          el.props.find(
            (p) => p.name.toLowerCase() === prop.name.toLowerCase()
          ) === undefined
      )

  if (!elementsToUse.length) return []

  const prompt = [
    `You will generate a single '${prop.name}' value for each element listed.`,
    `The property is defined as: '${prop.description}'.`,
    `All elements are of type: ${typeDef.name}.`,
    `Return ONLY a JSON object that validates against the provided JSON Schema.`,
    ``,
    `Elements to assign values to: ${elementsToUse
      .map((el) => `'${el.name}'`)
      .join(", ")}.`,
    ``,
    `For every element, include:`,
    `- name (must match one of the provided element names)`,
    `- value (the ${prop.name} value)`,
    `- reason (why that value was selected)`,
  ].join("\n")

  // JSON Schema (passed as `parameters`; helper converts to `schema`)
  const parameters = {
    type: "object",
    properties: {
      descriptions: {
        type: "array",
        items: {
          type: "object",
          properties: {
            name: {
              type: "string",
              enum: elementsToUse.map((el) => el.name),
              description: "The element name",
            },
            value: {
              type: "string",
              description: `The value for the '${prop.name}' property`,
              minLength: 1,
            },
            reason: {
              type: "string",
              description: "Short rationale for why this value was selected",
              minLength: 1,
            },
          },
          required: ["name", "value", "reason"],
          additionalProperties: false,
        },
        minItems: elementsToUse.length,
      },
    },
    required: ["descriptions"],
    additionalProperties: false,
  }

  try {
    const res = await createResponse({
      prompt,
      model: gptModel,
      jsonSchema: {
        name: "get_properties",
        description: `Generate '${prop.name}' values for the provided elements with reasons.`,
        strict: true,
        parameters, // callers use 'parameters' only
      },
      reasoning: { effort: "low" },
      store: false,
    })

    console.log("%cgot response", "color:green", res)

    const raw = res?.response?.output_text

    console.log("%craw", "color:blue", raw)

    const obj = JSON.parse(raw)

    console.log("%cparsed response", "color:orange", obj)

    return obj.descriptions
  } catch (err) {
    console.error("Error getting element properties", err)
    throw err
  }
}

const createGptFunctionForCheckingGoalCompleteness = () => {
  const functionName = "check_goal_completeness"

  const parameters = {
    type: "object",
    properties: {
      completion_percentage: {
        type: "integer",
        description: `A number between 1 and 100 which indicates how well the current views support the user's objective.`,
      },
      completion_recommendation: {
        type: "string",
        description: `A summary which lists the remaining questions that could be asked that would help support reaching the objective. You should include explaining what the views should focus on and the rationale for this in terms of how it supports achieving the objective. Put each view recipe on a new line.`,
      },
      completion_progress: {
        type: "string",
        description: `A summary which provide a summarised list explaining how well the views support the user's objective. Provide a fairly detailed analysis of how the existing views support the objective and how the views can be referenced by someone to provide a specific recommendation or summary, and what that summary might be. If any views are incomplete, explain why they are incomplete and how to complete them. You MUST check that any references to ArchiMate elements are valid ArchiMate elements, and not made up.`,
      },
    },
    required: [
      "completion_percentage",
      "completion_recommendation",
      "completion_progress",
    ],
    additionalProperties: false,
  }

  const functions = [
    {
      name: functionName,
      description: "Check if goal is completed",
      parameters: parameters,
    },
  ]

  return functions
}

const createGptFunctionForSuggestElementType = () => {
  const functionName = "suggest_element_type"

  const parameters = {
    type: "object",
    properties: {
      type: {
        type: "string",
        description: `The suggested ArchiMate element type to use.`,
        enum: palette.getElementTypeNames(),
      },
      reason: {
        type: "string",
        description: `The reason why this element type is useful for this specific view.`,
      },
    },
    required: ["type", "reason"],
  }

  const functions = [
    {
      name: functionName,
      description: "Suggest an element type",
      parameters: parameters,
    },
  ]

  return functions
}

const createGptFunctionForScopeOutcomeCheck = () => {
  const functionName = "check_scope_outcome"

  const parameters = {
    type: "object",
    properties: {
      defines_purpose: {
        type: "boolean",
        description: `Indicates if a objective has been sufficiently specified, i.e. what is the end goal and results that the user is trying to achieve.`,
      },
      context_matches_purpose: {
        type: "boolean",
        description: `Indicates if the context seems appropriate given the objective`,
      },
      context_purpose_feedback: {
        type: "string",
        description: `The feedback on the context in relation to the objective`,
      },
    },
    required: [
      "defines_purpose",
      "context_matches_purpose",
      "context_purpose_feedback",
    ],
  }

  const functions = [
    {
      name: functionName,
      description: "Check if scope outcome is satisfied",
      parameters: parameters,
    },
  ]

  return functions
}

const createGptFunctionsForBusinessProcess = () => {
  const functionName = "create_business_process"

  const parameters = {
    type: "object",
    properties: {
      elements: {
        type: "array",
        items: {
          properties: {
            name: {
              type: "string",
              description: `The name of the Business Function`,
            },
            inputs: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    description: `The name of the Business Object Input`,
                  },
                },
                required: ["name"],
              },
            },
            outputs: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    description: `The name of the Business Object Output`,
                  },
                },
              },
            },
            requirements: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    description: `The name of the Business Function Requirement`,
                  },
                  description: {
                    type: "string",
                    description: `The description of the Business Function Requirement`,
                  },
                },
              },
            },
          },
          required: ["name"],
        },
      },
    },
    required: ["elements"],
  }

  const functions = [
    {
      name: functionName,
      description: "Create Business Proxess",
      parameters: parameters,
    },
  ]

  return functions
}

const getJSONObj = (str) => {
  const pattern = /\{(?:[^{}]|{(?:[^{}]|{[^{}]*})*})*}/

  // Find the JSON object within the text.
  const match = str.match(pattern)

  if (match) {
    // Extract the JSON object as a string.
    const jsonString = match[0]

    // Parse the JSON object.
    const jsonObject = JSON.parse(jsonString)

    //console.log("JSON object:", jsonObject)
    return jsonObject
  } else {
    console.log("No JSON object found in the text.")
    return null
  }
}

function removeTrailingCommas(jsonString) {
  const regex = /,\s*([}\]])/g
  const result = jsonString.replace(regex, "$1")
  return result
}

const getLines = (content) => {
  const jsonArrayRegex = /\[(?:[^\[\]]|\[[^\[\]]*\])*\]/g
  const jsonArrayMatches = content.match(jsonArrayRegex)

  if (jsonArrayMatches && jsonArrayMatches.length > 0) {
    try {
      const jsonStr = removeTrailingCommas(jsonArrayMatches[0])
      const result = [{ type: "json", content: JSON.parse(jsonStr) }]
      //console.log("found JSON data", result)
      return result
    } catch (e) {
      console.log("Error parsing JSON", { e, jsonArrayMatches })
      return []
    }
  } else {
    console.log("No JSON array found.")
    return []
  }
}

export {
  //getChatCompletionResult,
  getLines,
  getJSONObj,
  createViewSetContext,
  checkModellingGoalCompleteness,
  createConsultingSummary,
  getSuggestedViewPrompt,
  generateInsightsAndActions,
  suggestElementType,
  sortViewsForPresentation,
  getObjectiveAndContextMessages,
  getViewSummary,
  getSuggestedTags,
  createElementProperties,
  convertToMessages,
  GPT_5,
  GPT_5_2,
  TEXT_EMBEDDING_ADA_002,
  MODEL_USAGE,
  AI_USAGE_POLICY,
  MAX_DESCRIPTION_WORDS,
  MAX_ELEMENTS,
  MAX_OPEN_QUESTIONS,
  MAX_MODEL_QUESTIONS,
  CAN_USE_O3,
  MAX_VIEWS,
  FUNCTION_RESEARCH_CONTEXT,
  FUNCTION_GENERATE_PROMPTS,
  FUNCTION_GENERATE_INSIGHTS_AND_ACTIONS,
  FUNCTION_CREATE_TITLE_AND_SUMMARY,
  FUNCTION_GET_BATCH_DESCRIPTIONS,
  FUNCTION_GET_SUGGESTED_TAGS,
  FUNCTION_GET_AUTO_COUNT_OF_NEXT_LEVEL,
  FUNCTION_CREATE_TEMPLATE,
  FUNCTION_GET_QUESTIONS_COMMON,
  FUNCTION_GET_MODEL_QUESTIONS,
  FUNCTION_GET_EXTENDED_QUESTIONS,
  FUNCTION_SORT_QUESTIONS,
  IS_AIM_TRIAL,
  QTY_SELECTION_ANY,
  QTY_SELECTION_UPTO,
  QTY_SELECTION_FIXED,
  getAIUsageParam,
  getModel,
}
